import { Button, Empty, Tag, Tooltip, Typography } from 'antd';
import { AlertCircle, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SortableList } from '@shared/components/SortableList';
import type { PropFieldModel } from '../types';
import { PROP_ICONS } from './propMeta';

const { Text } = Typography;

interface PropListProps {
  props: PropFieldModel[];
  selectedId: string | null;
  invalidIds: ReadonlySet<string>;
  onSelect: (id: string) => void;
  onReorder: (next: PropFieldModel[]) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

/** Orta sütun — propların sıralanabilir listesi; satır seç → sağ panelde düzenle. */
export function PropList({
  props,
  selectedId,
  invalidIds,
  onSelect,
  onReorder,
  onRemove,
  disabled,
}: PropListProps) {
  const { t } = useTranslation('component-studio');

  if (props.length === 0) {
    return <Empty description={t('builder.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <SortableList<PropFieldModel>
      items={props}
      getId={(p) => p.id}
      onReorder={onReorder}
      disabled={disabled}
      renderItem={(p) => {
        const isSelected = p.id === selectedId;
        const isInvalid = invalidIds.has(p.id);
        return (
          <div
            role="button"
            tabIndex={0}
            onClick={() => onSelect(p.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(p.id);
              }
            }}
            data-testid="prop-row"
            data-selected={isSelected ? 'true' : 'false'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 8,
              cursor: 'pointer',
              border: `1px solid ${isSelected ? 'var(--ds-brand)' : 'transparent'}`,
              background: isSelected ? 'var(--ds-surface-2)' : 'transparent',
            }}
          >
            <span style={{ color: 'var(--ds-text-tertiary)', display: 'flex' }}>{PROP_ICONS[p.type]}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <Text ellipsis style={{ fontWeight: 500 }}>
                {p.label.trim() || p.key.trim() || t(`builder.propType.${p.type}`)}
                {p.required ? <Text type="danger"> *</Text> : null}
              </Text>
              <div style={{ fontSize: 11, color: 'var(--ds-text-tertiary)' }}>
                {t(`builder.propType.${p.type}`)}
                {p.key.trim() ? ` · {{${p.key.trim()}}}` : ''}
              </div>
            </div>
            {isInvalid ? (
              <Tooltip title={t('builder.errors.title')}>
                <AlertCircle size={15} color="var(--ds-danger, #dc2626)" />
              </Tooltip>
            ) : null}
            <Tag style={{ marginInlineEnd: 0 }}>{p.type}</Tag>
            {!disabled ? (
              <Button
                type="text"
                size="small"
                danger
                icon={<Trash2 size={14} />}
                aria-label={t('builder.prop.remove')}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(p.id);
                }}
              />
            ) : null}
          </div>
        );
      }}
    />
  );
}
